import { Button, Flex, Typography, message } from "antd";
import { useState } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { membersService } from "../../service/members.service";
import { UserTier } from "../../slices/authSlice";

const { Text, Title } = Typography;

// What the customer is actually being asked to do, keyed on the cause the
// webhooks recorded — the same reasons PaymentFailingBanner keys on, because
// this takeover covers the banner and would otherwise send an SCA-blocked
// customer off to "fix" a card that was never declined.
const SUSPENDED_COPY = {
  payment_failed: {
    title: "Payment Failed",
    solo: "Your payment didn't go through, so your access is paused. Update your payment to restore it — access comes back automatically once the payment clears.",
    admin:
      "Your team's payment didn't go through, so access is paused for everyone. Update your payment to restore it — access comes back automatically once the payment clears.",
    member:
      "Your team's payment didn't go through, so access is paused. Please contact your group administrator — access comes back automatically once it's paid.",
    button: "Fix Payment",
  },
  payment_action_required: {
    title: "Verification Needed",
    solo: "Your bank needs to verify your payment, so your access is paused. Verify it to restore access — that happens automatically once the bank confirms.",
    admin:
      "Your bank needs to verify your team's payment, so access is paused for everyone. Verify it to restore access — that happens automatically once the bank confirms.",
    member:
      "Your team's payment needs bank verification, so access is paused. Please contact your group administrator — access comes back automatically once it clears.",
    button: "Verify Payment",
  },
  invoice_overdue: {
    title: "Invoice Overdue",
    solo: "Your invoice is overdue, so your access is paused. Pay it to restore access — that happens automatically once the payment clears.",
    admin:
      "Your team's invoice is overdue, so access is paused for everyone. Pay it to restore access — that happens automatically once the payment clears.",
    member:
      "Your team's invoice is overdue, so access is paused. Please contact your group administrator — access comes back automatically once it's paid.",
    button: "Pay Invoice",
  },
} as const;

const LAPSED_COPY = {
  title: "Subscription Inactive",
  solo: "Your subscription is inactive. Start it again to restore access.",
  admin:
    "Your group's subscription is inactive. Reactivate it to restore access for you and your whole team — access is restored automatically once payment goes through.",
  member: "Your group's subscription is currently inactive. Please contact your group administrator to restore access.",
  button: "Reactivate Subscription",
} as const;

const BillingInactiveModal = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);

  // Keyed on billing_suspended, not billing_payment_failing: access has to
  // have actually been withdrawn. A bank asking for 3DS opens a failing
  // window without suspending anyone, and that customer gets the banner, not
  // this takeover. Include is_group_admin: subscription deletion downgrades
  // the payer to FREE tier, and they must still get this screen (not the solo
  // paywall).
  const isSuspended = Boolean(userInfo.billing_suspended);
  const isGroupUser = userInfo.tier === UserTier.GROUP || userInfo.is_group_admin;
  const isAdmin = userInfo.is_group_admin;

  // A window is open, so the subscription is alive with something to pay. A
  // group that never got past its first checkout has no subscription to pay
  // against — it needs a payment link, and "access is paused for everyone"
  // would be news to a practice that never had access.
  const hasSubscription = isGroupUser ? userInfo.group_has_subscription !== false : true;
  const isPaymentFailing = Boolean(userInfo.billing_payment_failing) && hasSubscription;
  const reason = userInfo.billing_payment_failing_reason ?? "payment_failed";
  const copy = isPaymentFailing ? SUSPENDED_COPY[reason] ?? SUSPENDED_COPY.payment_failed : LAPSED_COPY;

  const handleReactivate = async () => {
    setLoading(true);
    // Open synchronously in the click gesture — Safari blocks window.open
    // after an await. Billing period is omitted so the backend restores the
    // group's previous cadence.
    const billingTab = window.open("", "_blank");
    try {
      // While the payment is still failing the subscription is alive with an
      // unpaid invoice, so pay THAT (intent="pay" routes to its hosted page).
      // Starting a fresh checkout here would leave the customer paying for two
      // subscriptions. Only once the subscription is actually gone is a new
      // one the way back.
      const url = isPaymentFailing
        ? (await membersService.openBillingPortal("pay")).portal_url
        : // Backend routes: overdue invoice, billing portal, or new subscription.
          (await membersService.generateGroupPaymentLink()).payment_link;
      if (billingTab) {
        billingTab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch {
      billingTab?.close();
      message.error("Couldn't open the billing page. Please try again or contact sales@getnudgeai.com.");
    } finally {
      setLoading(false);
    }
  };

  if (!isSuspended) return null;

  const helpLine = (
    <Text type="secondary" style={{ fontSize: 13 }}>
      Need help? Contact sales@getnudgeai.com
    </Text>
  );

  // Takeover that sits BELOW the app header (64px) so the existing header —
  // and its log out / contact us controls — stays visible and usable. Covers
  // content and banners beneath it, but stays UNDER antd's overlay base
  // (z-index 1000): any antd modal (e.g. the "New Version Available" update
  // modal) must render fully in front, or its full-viewport mask greys the
  // header while its dialog hides under this takeover — an unexplained,
  // unclickable grey band. The solo paywall isn't covered but suppressed at
  // the source in AuthLayout while billing is inactive.
  return (
    <div
      style={{
        position: "fixed",
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999,
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Flex vertical gap={20} align="center" style={{ maxWidth: 460, textAlign: "center" }}>
        <Title level={3} style={{ margin: 0 }}>
          {copy.title}
        </Title>
        {/* Only a member of a group has nobody to pay on their own behalf —
            everyone else, solo or admin, gets the self-serve route. */}
        {isGroupUser && !isAdmin ? (
          <>
            <Text style={{ fontSize: 16, color: "#4B5563" }}>{copy.member}</Text>
            {userInfo.group_name && <Text type="secondary">Group: {userInfo.group_name}</Text>}
            {helpLine}
          </>
        ) : (
          <>
            <Text style={{ fontSize: 16, color: "#4B5563" }}>{isGroupUser ? copy.admin : copy.solo}</Text>
            <Button
              type="primary"
              size="large"
              loading={loading}
              onClick={handleReactivate}
              style={{ minWidth: 240, height: 48 }}
            >
              {copy.button}
            </Button>
            {helpLine}
          </>
        )}
      </Flex>
    </div>
  );
};

export default BillingInactiveModal;
